import { useAuth } from 'src/composables/auth';
import { auth as useAuthStore } from 'src/store/auth';
import { computed } from 'vue';

import { resolveAuditorAccess } from '../../domain';

/**
 * Every auditor surface asks one of these rather than combining inputs itself, so a rule added to
 * `resolveAuditorAccess` reaches all of them at once.
 *
 * @returns {{
 *   canOperateAuditor: import('vue').ComputedRef<boolean>,
 *   canViewAuditorOutput: import('vue').ComputedRef<boolean>,
 *   canViewAuditorBehavior: import('vue').ComputedRef<boolean>,
 *   canRunCheckoutAudit: import('vue').ComputedRef<boolean>,
 *   canManageAuditorBehavior: import('vue').ComputedRef<boolean>,
 *   hasAuditorEntitlement: import('vue').ComputedRef<boolean>,
 *   isStaffBypass: import('vue').ComputedRef<boolean>,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useAuditorAccess() {
  const { userHasPrivilege } = useAuth();
  const authStore = useAuthStore();

  const access = computed(() => resolveAuditorAccess({
    hasPrivilege: (privilege) => !!userHasPrivilege(privilege),
    isEntitled: authStore.isNoteAuditorAvailable,
    isStaff: authStore.userIsAdmin,
  }));

  return {
    canOperateAuditor: computed(() => access.value.canOperateAuditor),
    canViewAuditorOutput: computed(() => access.value.canViewAuditorOutput),
    canViewAuditorBehavior: computed(() => access.value.canViewAuditorBehavior),
    canRunCheckoutAudit: computed(() => access.value.canRunCheckoutAudit),
    canManageAuditorBehavior: computed(() => access.value.canManageAuditorBehavior),
    hasAuditorEntitlement: computed(() => access.value.hasAuditorEntitlement),
    isStaffBypass: computed(() => access.value.isStaffBypass),
  };
}
